import { ActionIcon, Box, Modal } from "@mantine/core";
import { useDisclosure, useViewportSize } from "@mantine/hooks";
import { IconRotateClockwise, IconX } from "@tabler/icons-react";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

type LightboxImage = { src: string; alt: string };

type Ctx = { open: (image: LightboxImage) => void };

const ImageLightboxContext = createContext<Ctx | null>(null);

export function useImageLightbox() {
  const ctx = useContext(ImageLightboxContext);
  if (!ctx) throw new Error("useImageLightbox must be used inside ImageLightboxProvider");
  return ctx;
}

export function ImageLightboxProvider({ children }: { children: ReactNode }) {
  const [opened, { open: openModal, close }] = useDisclosure(false);
  const [image, setImage] = useState<LightboxImage | null>(null);
  const [rotated, setRotated] = useState(false);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const { width: vw, height: vh } = useViewportSize();

  const PAD = 64;
  const availW = Math.max(0, (vw || 0) - PAD);
  const availH = Math.max(0, (vh || 0) - PAD);

  let visualW = availW;
  let visualH = availH;
  if (natural && availW > 0 && availH > 0) {
    const ratio = rotated ? natural.h / natural.w : natural.w / natural.h;
    visualW = Math.min(availW, availH * ratio);
    visualH = visualW / ratio;
  }
  const imgW = rotated ? visualH : visualW;
  const imgH = rotated ? visualW : visualH;

  const open = useCallback(
    (img: LightboxImage) => {
      setImage(img);
      setRotated(false);
      setNatural(null);
      openModal();
    },
    [openModal],
  );

  const handleClose = useCallback(() => {
    close();
    setTimeout(() => {
      setImage(null);
      setRotated(false);
      setNatural(null);
    }, 200);
  }, [close]);

  const value = useMemo<Ctx>(() => ({ open }), [open]);

  return (
    <ImageLightboxContext.Provider value={value}>
      {children}
      <Modal
        opened={opened}
        onClose={handleClose}
        centered
        padding={0}
        withCloseButton={false}
        size="auto"
        radius="lg"
        overlayProps={{ backgroundOpacity: 0.75, blur: 4 }}
        styles={{
          root: { overflow: "hidden" },
          inner: { padding: 16, overflow: "hidden" },
          content: {
            backgroundColor: "transparent",
            boxShadow: "none",
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "calc(100vh - 32px)",
            overflow: "visible",
          },
          body: { padding: 0, overflow: "visible" },
          header: { display: "none" },
        }}
      >
        {image && (
          <Box
            pos="relative"
            style={{
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)",
              background: "var(--mantine-color-dark-9)",
              lineHeight: 0,
              touchAction: "none",
            }}
          >
            <TransformWrapper
              initialScale={1}
              minScale={1}
              maxScale={5}
              doubleClick={{ mode: "toggle", step: 1.5 }}
              wheel={{ step: 0.15 }}
              pinch={{ step: 5 }}
              panning={{ velocityDisabled: true }}
              centerOnInit
            >
              <TransformComponent
                wrapperStyle={{
                  width: visualW || undefined,
                  height: visualH || undefined,
                  maxWidth: "calc(100vw - 64px)",
                  maxHeight: "calc(100vh - 64px)",
                  cursor: "grab",
                }}
                contentStyle={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                }}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  draggable={false}
                  onLoad={(e) =>
                    setNatural({
                      w: e.currentTarget.naturalWidth,
                      h: e.currentTarget.naturalHeight,
                    })
                  }
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    width: imgW || "auto",
                    height: imgH || "auto",
                    userSelect: "none",
                    transform: rotated
                      ? "translate(-50%, -50%) rotate(90deg)"
                      : "translate(-50%, -50%)",
                    transformOrigin: "center center",
                    transition: "transform 250ms ease",
                  }}
                />
              </TransformComponent>
            </TransformWrapper>
            <ActionIcon
              onClick={() => setRotated((r) => !r)}
              aria-label={rotated ? "Voltar para a visualização horizontal" : "Girar imagem"}
              size="lg"
              radius="xl"
              variant="filled"
              style={{
                position: "absolute",
                top: 8,
                right: 56,
                zIndex: 10,
                backgroundColor: "rgba(0,0,0,0.6)",
              }}
            >
              <IconRotateClockwise size={20} color="white" />
            </ActionIcon>
            <ActionIcon
              onClick={handleClose}
              aria-label="Fechar"
              size="lg"
              radius="xl"
              variant="filled"
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 10,
                backgroundColor: "rgba(0,0,0,0.6)",
              }}
            >
              <IconX size={20} color="white" />
            </ActionIcon>
          </Box>
        )}
      </Modal>
    </ImageLightboxContext.Provider>
  );
}
